import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL, SERVICE_AREA } from '../../data/site';
import { logConversionSkipped, trackEvent, trackLeadConversion } from '../../utils/analytics';
import { MAX_IMAGE_MB, validateImage } from '../../services/imageService';
import { telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const emptyForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  description: ''
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface Selected {
  file: File;
  preview: string;
  /** Udfyldt hvis filen ikke kan bruges — blokerer indsendelse indtil den fjernes. */
  problem: string | null;
}
export function ContactSection() {
  const {
    createEnquiry,
    settings
  } = useAdminData();
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState<Selected[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  /** Id på en påbegyndt, ufærdig henvendelse — genbruges ved nyt forsøg. */
  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const invalid = selected.filter(item => item.problem);
  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Skriv dit navn.';
    if (form.phone.replace(/[^\d]/g, '').length < 8) next.phone = 'Skriv et gyldigt telefonnummer.';
    if (form.email && !emailPattern.test(form.email.trim())) next.email = 'Skriv en gyldig e-mailadresse.';
    if (!form.description.trim()) next.description = 'Beskriv kort opgaven.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function removeImage(index: number) {
    setSubmitError('');
    setSelected(current => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  }
  return <section id="kontakt" className="scroll-mt-24" data-id="element-342" data-mp="2x2kggb13b796">
      <div className="mx-auto max-w-content px-5 py-16 md:py-24 lg:px-8" data-id="element-343" data-mp="2x1si4sg3yvf7">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-344" data-mp="3bqop0m6fi2e8">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-345" data-mp="36sdiolfcea0r">
            <h2 className="font-display text-[clamp(2rem,5.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-346" data-mp="3ksxzxd5lzxs5">
              Hvad skal du have lavet?
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-ink-soft" data-id="element-347" data-mp="3fsaa2ut49mzi">
              Beskriv kort din opgave og send gerne et par billeder. Så kan jeg bedre vurdere, hvad
              der skal til. Du kan også bare ringe.
            </p>
            <p className="mt-4 max-w-md border-l-2 border-accent pl-5 text-[16px] leading-relaxed text-ink-soft" data-id="element-348" data-mp="z2tx822dtl8">
              Du behøver ikke vide præcis, hvad opgaven kræver. Beskriv problemet, som du ser det, og
              send gerne et par billeder – så tager vi den derfra.
            </p>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-ink-muted" data-id="element-349" data-mp="3sbnrrqsdna3p">
              Jeg kører primært opgaver i {SERVICE_AREA}.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-350" data-mp="3jc0zbuhmlems">
              {settings.phone && <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-351" data-mp="35crhkqdrucgp">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-352" data-mp="13kmrzljqvr95" />
                  {settings.phone}
                </LinkButton>}
              <LinkButton href={`mailto:${CONTACT_EMAIL}`} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-353" data-mp="17kdqkbfijbo8">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-354" data-mp="duwkn66of617" />
                {CONTACT_EMAIL}
              </LinkButton>
            </div>

            <p className="mt-8 max-w-md border-l-2 border-line pl-5 text-[15px] leading-relaxed text-ink-muted" data-id="element-355" data-mp="3zcp4kw0y9cz">
              Du får en pris eller et tilbud, inden jeg går i gang — så ved du, hvad din opgave
              koster. Registreret med CVR {settings.cvr}.
            </p>
          </div>

          {sent ? <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease
        }} role="status" className="flex flex-col items-start rounded-card border border-line bg-white p-8 md:p-12" data-id="element-356" data-mp="3mdc93lrt8z10">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-357" data-mp="38ew1ztq1d6ic">
                <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-358" data-mp="1rw2ds0mccv7k" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-359" data-mp="1wvfd3kuefur1">
                Tak for din henvendelse.
              </h3>
              <p className="mt-3 text-[17px] text-ink-soft" data-id="element-360" data-mp="21u03ytnrh22t">
                LYKKEVÆRK vender tilbage hurtigst muligt.
              </p>
              <Button variant="secondary" size="md" className="mt-8" onClick={() => setSent(false)} data-id="element-361" data-mp="195uho2h2750v">
                Send en opgave mere
              </Button>
              <p className="mt-6 text-[15px] text-ink-muted" data-id="element-362" data-mp="1e3ctq1qdi0d3">
                Har du travlt med opgaven, må du gerne bare ringe.
              </p>
            </motion.div> : <form noValidate className="rounded-card border border-line bg-white p-6 md:p-9" onSubmit={async event => {
          event.preventDefault();
          if (submitting) return;
          if (!validate()) return;
          if (invalid.length > 0) {
            setSubmitError(invalid[0].problem ?? '');
            return;
          }
          setSubmitting(true);
          setSubmitError('');
          const result = await createEnquiry({
            ...form,
            files: selected.map(item => item.file),
            draftId
          });
          setSubmitting(false);
          if (!result.ok) {
            // Samme dokument genbruges ved næste forsøg, så der ikke opstår dubletter.
            setDraftId(result.draftId);
            setSubmitError(result.error ?? 'Henvendelsen kunne ikke sendes. Prøv igen, eller ring til os i stedet.');
            logConversionSkipped(result.error ?? 'indsendelsen fejlede');
            return;
          }

          // Herfra er henvendelsen oprettet, alle billeder uploadet, stierne
          // gemt og statussen sat til "Ny henvendelse".
          void trackEvent('generate_lead', {
            lead_source: 'website_contact_form',
            form_name: 'contact_form'
          });
          // Google Ads: én fuldført henvendelse = én konvertering.
          if (result.draftId) trackLeadConversion(result.draftId);
          selected.forEach(item => URL.revokeObjectURL(item.preview));
          setSelected([]);
          setForm(emptyForm);
          setErrors({});
          setDraftId(undefined);
          setSent(true);
        }} data-id="element-363" data-mp="lfgwmvww66fs">
              <p className="mb-5 text-sm text-ink-muted" data-id="element-364" data-mp="3cmlw11zbttse">
                Felter markeret med <span className="text-accent" data-id="element-365" data-mp="47tx7agio321">*</span> skal udfyldes.
              </p>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-366" data-mp="91eokjnltys9">
                <TextField id="navn" label="Navn" required autoComplete="name" placeholder="Dit navn" value={form.name} error={errors.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} data-id="element-367" data-mp="253i63y5fpym5" />
                <TextField id="telefon" label="Telefonnummer" required type="tel" inputMode="tel" autoComplete="tel" placeholder="12 34 56 78" value={form.phone} error={errors.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} data-id="element-368" data-mp="1qc99v6711f01" />
                <TextField id="email" label="E-mail" type="email" autoComplete="email" placeholder="navn@gmail.com" value={form.email} error={errors.email} onChange={event => setForm({
              ...form,
              email: event.target.value
            })} data-id="element-369" data-mp="12pate8iwq724" />
                <TextField id="omraade" label="Adresse eller område" autoComplete="street-address" placeholder="Fx Grenaa, Rønde eller Ebeltoft" value={form.address} onChange={event => setForm({
              ...form,
              address: event.target.value
            })} data-id="element-370" data-mp="3japom2t1tqfu" />
              </div>

              <TextArea id="beskrivelse" label="Beskrivelse af opgaven" required className="mt-5" placeholder="Fortæl kort hvad der skal laves." value={form.description} error={errors.description} onChange={event => setForm({
            ...form,
            description: event.target.value
          })} data-id="element-371" data-mp="5vjkv23xp5vo" />

              <div className="mt-5" data-id="element-372" data-mp="2zfkgsz987ngt">
                <p className="mb-1.5 text-sm font-medium text-ink" data-id="element-373" data-mp="2zgls94u5mbz2">Upload billeder</p>
                <label htmlFor="billeder" className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-374" data-mp="34e3zmqsiwj4y">
                  <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-375" data-mp="3ibaield8yhu6" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-376" data-mp="3na3lrkytksgm">Vælg billeder</span>
                  <span className="text-sm text-ink-muted" data-id="element-377" data-mp="3s59i7dpulpiy">
                    Valgfrit — billeder op til {MAX_IMAGE_MB} MB
                  </span>
                  <input id="billeder" type="file" multiple accept="image/*" className="sr-only" disabled={submitting} onChange={event => {
                const input = event.target;
                const picked = Array.from(input.files ?? []).map(file => ({
                  file,
                  preview: URL.createObjectURL(file),
                  problem: validateImage(file)
                }));
                setSelected(current => [...current, ...picked]);
                setSubmitError('');
                input.value = '';
              }} data-id="element-378" data-mp="5wrfjofwgehc" />
                </label>

                {invalid.length > 0 && <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-379" data-mp="24bxio4j6pgdv">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-380" data-mp="2wvnu9qrlcvuc" />
                    <span data-id="element-381" data-mp="2s1rymuysvl2c">
                      {invalid.map(item => <span key={item.file.name} className="block" data-id="element-382" data-mp="q6vhyfq7ttg0">
                          {item.problem}
                        </span>)}
                    </span>
                  </p>}

                {selected.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-383" data-mp="leu7mzclycsm">
                    {selected.map((item, index) => <li key={`${item.file.name}-${index}`} className={`overflow-hidden rounded-xl border ${item.problem ? 'border-danger' : 'border-line'}`} data-id="element-384" data-mp="6h1x6llx4ela" data-mpi={index}>
                        <div className="relative" data-id="element-385" data-mp="2fgf8luj1a48">
                          <img src={item.preview} alt={item.file.name} className="aspect-square w-full object-cover" data-id="element-386" data-mp="2i2vrazeazyl" />
                          <button type="button" onClick={() => removeImage(index)} aria-label={`Fjern billede ${item.file.name}`} className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-387" data-mp="26xo90qxdbx9a">
                            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-388" data-mp="2pqs66m9sos8n" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2" data-id="element-389" data-mp="2ypvq1sgsdbo7">
                          <span className={`truncate text-xs ${item.problem ? 'text-danger' : 'text-ink-muted'}`} data-id="element-390" data-mp="2uo1t9v9portb">
                            {item.file.name}
                          </span>
                          <button type="button" onClick={() => removeImage(index)} className="shrink-0 text-xs font-medium text-accent hover:text-accent-deep" data-id="element-391" data-mp="10bbx16vvf6ir">
                            Fjern
                          </button>
                        </div>
                      </li>)}
                  </ul>}
              </div>

              {Object.keys(errors).length > 0 && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-392" data-mp="iylso6bhrmog">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-393" data-mp="rz0gbcbxhdv4" />
                  Tjek de markerede felter, før du sender.
                </p>}

              {submitError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-394" data-mp="4bixi6an5q4a">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-395" data-mp="3svb7722ifxum" />
                  {submitError}
                </p>}

              <Button type="submit" size="lg" className="mt-7 w-full text-lg" disabled={submitting || invalid.length > 0} data-id="element-396" data-mp="3iy6lf3cku4ec">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-397" data-mp="357tdh0zoqpp0" />
                    {selected.length > 0 ? 'Sender og uploader billeder …' : 'Sender …'}
                  </> : 'Send din opgave'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}